import { useState, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { Database, Plus, Lock, Terminal, Copy, Check, CheckCircle, HelpCircle, RotateCw } from 'lucide-react';
import { format } from 'date-fns';
import { useReactTable, getCoreRowModel, createColumnHelper, ColumnDef } from '@tanstack/react-table';
import ResourceForm from '../components/ResourceForm';
import { resourcesApi, accessRequestsApi } from '../services/api';
import { Resource, AccessRequest } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { Button, Card, EmptyState, IconButton, Modal, PageHeader, PaginationBar, SearchField, StatusBadge, TanstackTable, StatCard } from '@/components/ds-zta';
import StatValue from '@/components/StatValue';
import { useUrlState } from '@/lib/useUrlState';
import { ROWS_PER_PAGE_OPTIONS, pageOffset, readPage, readRows } from '@/lib/urlPaging';
import { healthRank, timeValue } from '@/lib/sortValues';
import { copyWithToast } from '@/lib/copy';
import { BRAND } from '@/brand';

const columnHelper = createColumnHelper<Resource>();

/** Formats a timestamp, or returns "—" when it is missing or unparseable
 *  (date-fns `format` throws "Invalid time value" on an Invalid Date). "—" is
 *  the one no-value token; "Never" means never-expires only (design eval F47). */
const formatDateSafe = (value: string | number | Date | null | undefined, pattern: string) => {
    if (value === null || value === undefined || value === '') return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : format(d, pattern);
};

// protocolMeta maps a DB protocol to a display label + conventional local port.
function protocolMeta(protocol?: string) {
    const p = (protocol || '').toLowerCase();
    if (p.includes('mysql') || p.includes('maria')) return { label: 'MySQL', port: 3306 };
    if (p.includes('mongo')) return { label: 'MongoDB', port: 27017 };
    if (p.includes('redis')) return { label: 'Redis', port: 6379 };
    return { label: 'PostgreSQL', port: 5432 };
}

function clientCommand(protocol: string | undefined, localPort: number, user?: string, db?: string) {
    const p = (protocol || '').toLowerCase();
    const u = user || '<user>';
    const d = db || '<database>';
    if (p.includes('mysql') || p.includes('maria')) return `mysql -h 127.0.0.1 -P ${localPort} -u ${u} ${d}`;
    if (p.includes('mongo')) return `mongosh "mongodb://127.0.0.1:${localPort}/${d}"`;
    if (p.includes('redis')) return `redis-cli -h 127.0.0.1 -p ${localPort}`;
    return `psql -h localhost -p ${localPort} -U ${u} -d ${d}`;
}

// CopyLine renders a copyable command line.
function CopyLine({ cmd }: { cmd: string }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        if (!(await copyWithToast(cmd, { success: false }))) return;
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    return (
        <div className="relative">
            <pre className="overflow-x-auto rounded-lg border border-[var(--d2d-border)] bg-[var(--d2d-bg-secondary)] text-[var(--d2d-text-primary)] text-[13px] leading-relaxed p-3 pr-12"><code>{cmd}</code></pre>
            <IconButton
                aria-label="Copy"
                tooltip="Copy"
                size="sm"
                onClick={copy}
                className="absolute right-2 top-2"
                icon={copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            />
        </div>
    );
}

// ConnectModal is always mounted (returns null when closed) so React never
// mounts/unmounts a large overlay in a way that thrashes the reconciler.
function ConnectModal({ resource, onClose }: { resource: Resource | null; onClose: () => void }) {
    if (!resource) return null;
    const meta = protocolMeta(resource.endpoint?.protocol as string);
    const localPort = meta.port;
    return (
        <Modal
            dismissOnOverlay={false}
            open
            onClose={onClose}
            title={
                <span className="flex items-center gap-3">
                    <span className="p-2 bg-[var(--ds-color-bg-subtle)] rounded-lg"><Terminal className="h-5 w-5 text-[var(--d2d-text-primary)]" /></span>
                    Connect to {resource.external_id}
                </span>
            }
            description={<>{meta.label} · {resource.endpoint?.host}:{resource.endpoint?.port || meta.port}</>}
            footer={<Button variant="primary" onClick={onClose}>Done</Button>}
        >
                <div className="space-y-4 text-sm">
                    <div>
                        <p className="font-medium text-[var(--d2d-text-secondary)] mb-1">1. Open a secure tunnel with the {BRAND.name} CLI (<code>iap</code>)</p>
                        <CopyLine cmd={`./iap db connect ${resource.external_id}`} />
                        <p className="mt-1 text-xs text-[var(--d2d-text-muted)]">Creates a local tunnel (e.g. <code>localhost:{localPort}</code>) — the real credentials are never exposed.</p>
                    </div>
                    <div>
                        <p className="font-medium text-[var(--d2d-text-secondary)] mb-1">2. Connect with your client</p>
                        <CopyLine cmd={clientCommand(resource.endpoint?.protocol as string, localPort, resource.endpoint?.username as string, resource.endpoint?.database as string)} />
                        <p className="mt-1 text-xs text-[var(--d2d-text-muted)]">Or point any GUI client (DBeaver, TablePlus, …) at <code>127.0.0.1:{localPort}</code>.</p>
                    </div>
                </div>
        </Modal>
    );
}

export default function DatabaseResources() {
    // Search and paging live in the URL (design eval F24). This list used to
    // stop at the first 50 databases with nothing saying so (F9); it now pages
    // through /resources/by-role. That endpoint takes no search param, so the
    // search filters the loaded page and says so (F7).
    const [url, setUrl] = useUrlState({ q: '', page: '1', rows: '50' });
    const searchTerm = url.q;
    const setSearchTerm = (next: string) => setUrl({ q: next }, { replace: true });
    const page = readPage(url.page);
    const rowsPerPage = readRows(url.rows, 50);
    const [connectModal, setConnectModal] = useState<Resource | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const { isAdmin, canManageResources } = usePermissions();
    const isUserAdmin = isAdmin();

    const { data, isLoading, isFetching, isError, refetch } = useQuery(
        ['database-resources', page, rowsPerPage],
        () => resourcesApi.listByRole('database', rowsPerPage, pageOffset(page, rowsPerPage)).then(res => res.data),
        { keepPreviousData: true },
    );

    const { data: myApprovalsData } = useQuery(
        ['my-approvals-database'],
        () => accessRequestsApi.getMyApprovals(),
        { refetchInterval: 30000 },
    );

    const myApprovals = useMemo<AccessRequest[]>(() => myApprovalsData?.data?.items || [], [myApprovalsData]);
    const databases = useMemo<Resource[]>(() => data?.resources || [], [data]);
    const total = Math.max(data?.total ?? 0, databases.length);
    const paged = total > databases.length;

    const { data: accessStatusMap } = useQuery(
        ['database-access-status', databases.map(r => r.id).join(',')],
        async () => {
            const statusMap: Record<string, boolean> = {};
            await Promise.all(databases.map(async (resource) => {
                try {
                    const resp = await resourcesApi.getAccessStatus(resource.id);
                    statusMap[resource.id] = resp.data.has_access === true;
                } catch { statusMap[resource.id] = false; }
            }));
            return statusMap;
        },
        { enabled: databases.length > 0 && !isUserAdmin, refetchInterval: 30000 },
    );

    const filteredDatabases = useMemo(() => databases.filter((r: Resource) =>
        r.external_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.endpoint?.host as string)?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
        (r.endpoint?.database as string)?.toLowerCase()?.includes(searchTerm.toLowerCase())
    ), [databases, searchTerm]);

    const hasValidApproval = useCallback((resourceId: string): boolean => {
        if (isUserAdmin) return true;
        if (accessStatusMap && accessStatusMap[resourceId]) return true;
        const approval = myApprovals.find((req) => req.resource_id === resourceId);
        if (!approval || approval.status !== 'approved') return false;
        if (approval.approved_at && approval.duration_seconds) {
            const expiry = new Date(approval.approved_at).getTime() + approval.duration_seconds * 1000;
            if (Date.now() >= expiry) return false;
        }
        return true;
    }, [isUserAdmin, accessStatusMap, myApprovals]);

    const columns = useMemo<ColumnDef<Resource, any>[]>(() => [
        columnHelper.display({
            id: 'name',
            header: 'Database',
            meta: { sortable: true, sortValue: (r) => (r.external_id || '').toLowerCase() },
            cell: ({ row }) => {
                const meta = protocolMeta(row.original.endpoint?.protocol as string);
                return (
                    <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                            <div className="h-8 w-8 rounded-full bg-[var(--ds-color-bg-subtle)] flex items-center justify-center">
                                <Database className="h-5 w-5 text-[var(--d2d-text-primary)]" />
                            </div>
                        </div>
                        <div className="ml-4 w-[160px]">
                            <div className="w-full text-dense font-medium text-[var(--d2d-text-primary)] truncate font-mono tabular-nums">{row.original.external_id}</div>
                            <div className="w-full text-dense text-[var(--d2d-text-muted)]">{meta.label}</div>
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'host',
            header: 'Host',
            meta: { sortable: true, sortValue: (r) => String(r.endpoint?.host ?? '').toLowerCase() },
            cell: ({ row }) => {
                const meta = protocolMeta(row.original.endpoint?.protocol as string);
                return (
                    <div className="text-dense text-[var(--d2d-text-primary)] w-[220px] truncate font-mono">
                        {row.original.endpoint?.host}:{row.original.endpoint?.port || meta.port}
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'database',
            header: 'Database',
            meta: { sortable: true, sortValue: (r) => String(r.endpoint?.database ?? '').toLowerCase() },
            cell: ({ row }) => (
                <span className="text-dense text-[var(--d2d-text-primary)]">
                    {row.original.endpoint?.database || '—'}
                    {row.original.endpoint?.username ? <span className="text-[var(--d2d-text-muted)]"> · {row.original.endpoint.username as string}</span> : null}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'status',
            header: 'Status',
            meta: { sortable: true, sortValue: (r) => healthRank(r.health_status) },
            cell: ({ row }) => {
                const h = row.original.health_status;
                const healthy = h === 'healthy';
                const label = healthy ? 'Online' : (h || 'Unknown');
                return (
                    <StatusBadge value={h || 'unknown'} className="capitalize">{label}</StatusBadge>
                );
            },
        }),
        columnHelper.display({
            id: 'last_seen',
            header: 'Last Seen',
            meta: { sortable: true, sortValue: (r) => timeValue(r.last_seen) },
            cell: ({ row }) => (
                <span className="text-dense text-[var(--d2d-text-muted)]">
                    {formatDateSafe(row.original.last_seen, 'MMM d, HH:mm')}
                </span>
            ),
        }),
        columnHelper.display({
            id: 'actions',
            header: () => <span className="block text-right">Actions</span>,
            cell: ({ row }) => {
                const hasAccess = hasValidApproval(row.original.id);
                return (
                    <div className="flex flex-col items-end gap-1">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setConnectModal(row.original)}
                            disabled={!hasAccess}
                            title={!hasAccess ? 'Access request approval required' : 'Show connect command'}
                            leadingIcon={hasAccess ? <Terminal className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                        >
                            Connect
                        </Button>
                        {!hasAccess && (
                            <Link to="/access-requests" className="text-micro text-[var(--d2d-text-primary)] hover:underline flex items-center gap-1">
                                <Lock className="h-3 w-3 flex-shrink-0" aria-hidden="true" />Request Access
                            </Link>
                        )}
                    </div>
                );
            },
        }),
    ], [hasValidApproval]);

    const table = useReactTable({ data: filteredDatabases, columns, getCoreRowModel: getCoreRowModel() });

    const healthyCount = useMemo(() => databases.filter(r => r.health_status === 'healthy').length, [databases]);
    const unhealthyCount = useMemo(() => databases.filter(r => r.health_status === 'unhealthy').length, [databases]);

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <PageHeader
                title="Databases"
                icon={<Database />}
                description="Connect to databases through a short-lived, credential-brokered CLI tunnel — no real passwords exposed."
                onRefresh={() => refetch()}
                refreshing={isFetching}
                primaryAction={
                    canManageResources() ? (
                        <Button variant="primary" onClick={() => setShowAddForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                            Add Database
                        </Button>
                    ) : undefined
                }
            />

            {showAddForm && (
                <ResourceForm initialType="database" onClose={() => setShowAddForm(false)} onSuccess={() => { refetch(); setShowAddForm(false); }} />
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <StatCard icon={<Database />} label="Total Databases" value={total} />
                <StatCard icon={<CheckCircle />} accent="success" label="Healthy" value={<StatValue value={healthyCount} caption={paged ? 'on this page' : undefined} />} />
                <StatCard icon={<HelpCircle />} label="Unhealthy / Unknown" value={<StatValue value={unhealthyCount + (databases.length - healthyCount - unhealthyCount)} caption={paged ? 'on this page' : undefined} />} />
            </div>

            {/* Search */}
            <Card className="p-4 overflow-visible">
                <SearchField
                  label="Search databases by name, host, or database"
                  placeholder="Search databases by name, host, or database..."
                  value={searchTerm}
                  onChange={(value) => setSearchTerm(value)}
                  help={paged ? `Searches the ${databases.length} databases on this page.` : undefined}
                />
            </Card>

            {/* Table */}
            <Card className="overflow-hidden">
                {isError ? (
                    <EmptyState
                        icon={<Database />}
                        title="Failed to load databases"
                        description="Something went wrong while loading database resources."
                        action={
                            <Button variant="secondary" onClick={() => refetch()} leadingIcon={<RotateCw className="h-4 w-4" />}>
                                Retry
                            </Button>
                        }
                    />
                ) : (
                    <div className="overflow-x-auto">
                        <TanstackTable
                            aria-label="Databases"
                            table={table}
                            loading={isLoading}
                            empty={
                                searchTerm ? (
                                    <EmptyState
                                        icon={<Database />}
                                        title={paged ? `No databases on this page match "${searchTerm}"` : `No databases match "${searchTerm}"`}
                                        description="Try searching by a different name, host, or database."
                                    />
                                ) : (
                                    <EmptyState
                                        icon={<Database />}
                                        title="No databases yet"
                                        description="Add a database to connect through a credential-brokered tunnel."
                                        action={
                                            canManageResources() ? (
                                                <Button onClick={() => setShowAddForm(true)} leadingIcon={<Plus className="h-4 w-4" />}>
                                                    Add Database
                                                </Button>
                                            ) : undefined
                                        }
                                    />
                                )
                            }
                        />
                    </div>
                )}
            </Card>

            {total > 0 && !isError && (
                <PaginationBar
                    page={page}
                    totalRows={total}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(p) => setUrl({ page: p })}
                    onRowsPerPageChange={(n) => setUrl({ rows: n, page: null })}
                    rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                />
            )}

            <ConnectModal resource={connectModal} onClose={() => setConnectModal(null)} />
        </div>
    );
}
